import React, { useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import { TextForm } from 'features/common';
import moment from 'moment';
import T from 'i18n';

const EventsDateTimeForm = ({
  dateTimeVal,
  setDateTimeVal,
  errors,
  register,
  trigger,
  currentSelection,
  setCurrentSelection = () => {},
  focusDateField = false,
  inline = false,
}: any) => {
  useEffect(() => {
    if (dateTimeVal?.time && dateTimeVal.time.length >= 5) {
      trigger('time');
    }
  }, [dateTimeVal?.time, trigger]);

  const isValidDate = (val: moment.MomentInput) => {
    if (!val) return T.translate('case.dateRequired');

    if (typeof val === 'string') {
      if (!moment(val, 'DD/MM/YYYY', true).isValid()) {
        return T.translate('case.invalidDate');
      }
    }

    return true;
  };
  const isValidTime = (val: any) => {
    if (!val) return true; // Time is not required
    if (!moment(val, 'HH:mm:ss', true).isValid()) {
      return T.translate('case.invalidTime');
    }

    return true;
  };

  // Add this first useEffect to validate on initial load and focus changes
  useEffect(() => {
    // If date already has a value, validate it
    if (dateTimeVal?.date && dateTimeVal.date.length >= 8) {
      trigger('date');
    }
  }, []);

  // Keep the existing useEffect for validating as user types
  useEffect(() => {
    // Validate date whenever it changes and has enough characters
    if (dateTimeVal?.date && dateTimeVal.date.length >= 8) {
      trigger('date');
    }
  }, [dateTimeVal?.date, trigger]);

  const isValidKey = (event: any) => {
    const key = window.event ? event.keyCode : event.which;
    const {
      keyCode,
      ctrlKey,
      metaKey,
      shiftKey,
      target: { value },
    } = event;
    if (
      (!value ||
        (value.match(new RegExp('/', 'g')) || []).length === 2 ||
        (value.match(new RegExp(':', 'g')) || []).length === 2 ||
        value[value.length - 1] === ':' ||
        value[value.length - 1] === '/') &&
      (keyCode === 110 || keyCode === 111 || keyCode === 190 || keyCode === 191)
    )
      event.preventDefault();
    else if (
      keyCode === 8 ||
      keyCode === 9 ||
      keyCode === 46 ||
      keyCode === 37 ||
      keyCode === 39 ||
      keyCode === 35 ||
      keyCode === 36 ||
      keyCode === 190 ||
      keyCode === 191 ||
      keyCode === 110 ||
      keyCode === 111 ||
      ((ctrlKey || metaKey) && (keyCode === 65 || keyCode === 67 || keyCode === 86))
    )
      return true;
    else if ((shiftKey || key < 48 || key > 57) && (key < 96 || key > 105)) event.preventDefault();
    else return true;
  };

  const handleDateTimechange = ({ target: { value } }: any, key: any, seprator: any) => {
    if ((key === 'date' && value?.length > 10) || (key === 'time' && value.length > 8)) return;
    else if (value.length < dateTimeVal[key as keyof typeof dateTimeVal].length) {
      setDateTimeVal({
        ...dateTimeVal,
        [key]: value,
      });
      if (
        errors &&
        ((Object.keys(errors).includes('date') && key === 'date' && value.length === 0) ||
          (Object.keys(errors).includes('time') && key === 'time' && value.length === 0))
      ) {
        setTimeout(() => trigger(['date', 'time']), 0);
      }
    } else if (value.length === 2 || value.length === 5) {
      if (value.length === 2 && (value[1] === '/' || value[1] === '.')) {
        setDateTimeVal({ ...dateTimeVal, [key]: '0' + value[0] + seprator });
      } else if (value.length === 5 && (value[4] === '/' || value[4] === '.')) {
        setDateTimeVal({
          ...dateTimeVal,
          [key]: value.slice(0, 3) + '0' + value[3] + seprator,
        });
      } else setDateTimeVal({ ...dateTimeVal, [key]: value + seprator });
    } else if (key === 'date' && value.length === 4 && parseInt(value[3]) > 1) {
      setDateTimeVal({
        ...dateTimeVal,
        [key]: value.slice(0, 3) + '0' + value[3] + seprator,
      });
    } else if (value.length > 2 && value[2] !== seprator && !value.slice(0, 4).includes('/'))
      setDateTimeVal({
        ...dateTimeVal,
        [key]: value.slice(0, 2) + seprator + value.slice(2),
      });
    else if (value.length > 5 && value[5] !== seprator && !value.slice(4, 7).includes('/'))
      setDateTimeVal({
        ...dateTimeVal,
        [key]: value.slice(0, 5) + seprator + value.slice(5),
      });
    else {
      setDateTimeVal({
        ...dateTimeVal,
        [key]: value,
      });
      if (
        errors &&
        ((Object.keys(errors).includes('date') && key === 'date' && value.length === 10) ||
          (Object.keys(errors).includes('time') && key === 'time' && value.length === 8))
      )
        trigger(['date', 'time']);
    }
  };
  return (
    <>
      {inline ? (
        // Inline layout - Date and time in a single row
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={6}>
            <Box sx={{ minHeight: '80px' }}>
              <TextForm
                {...register('date', {
                  validate: {
                    isValidDate: (val: moment.MomentInput) => {
                      if (!val) return T.translate('case.dateRequired');
                      if (typeof val === 'string') {
                        if (!moment(val, 'DD/MM/YYYY', true).isValid()) {
                          return T.translate('case.invalidDate');
                        }
                      }
                      return true;
                    },
                  },
                  required: T.translate('case.dateRequired'),
                  onChange: (e: any) => {
                    handleDateTimechange(e, 'date', '/');
                    if (e.target.value && e.target.value.length >= 8) {
                      setTimeout(() => trigger('date'), 0);
                    }
                  },
                })}
                label={T.translate('case.dateWithFormat')}
                placeholder={T.translate('case.datePlaceholder')}
                value={dateTimeVal.date}
                onKeyDown={(e: any) => isValidKey(e)}
                errors={errors}
                onFocus={(e: any) => setCurrentSelection(e.target.name)}
                autofocus={(!currentSelection && focusDateField) || currentSelection === 'date'}
                errorProps={{ sx: { position: 'absolute' } }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ minHeight: '80px' }}>
              <TextForm
                {...register('time', {
                  validate: {
                    isValidTime: isValidTime,
                  },
                  onChange: (e: any) => {
                    handleDateTimechange(e, 'time', ':');
                    if (e.target.value && e.target.value.length >= 5) {
                      setTimeout(() => trigger('time'), 0);
                    }
                  },
                })}
                label={T.translate('case.time')}
                placeholder={T.translate('case.timePlaceholder')}
                value={dateTimeVal.time}
                onKeyDown={(e: any) => isValidKey(e)}
                errors={errors}
                onFocus={(e: any) => setCurrentSelection(e.target.name)}
                autofocus={currentSelection === 'time'}
                errorProps={{ sx: { position: 'absolute' } }}
              />
            </Box>
          </Grid>
        </Grid>
      ) : (
        // Default layout - Date and time in separate rows
        <>
          <Grid item xs={6}>
            <TextForm
              {...register('date', {
                // Same date registration code as above
                validate: {
                  isValidDate: isValidDate,
                },
                required: T.translate('case.dateRequired'),
                onChange: (e: any) => {
                  handleDateTimechange(e, 'date', '/');
                  if (e.target.value && e.target.value.length >= 8) {
                    setTimeout(() => trigger('date'), 0);
                  }
                },
              })}
              /*@ts-ignore*/
              label={T.translate('case.dateWithFormat')}
              placeholder={T.translate('case.datePlaceholder')}
              value={dateTimeVal.date}
              onKeyDown={(e: any) => isValidKey(e)}
              errors={errors}
              onFocus={(e: any) => setCurrentSelection(e.target.name)}
              autofocus={(!currentSelection && focusDateField) || currentSelection === 'date'}
            />
          </Grid>
          <Grid item xs={6}>
            <TextForm
              {...register('time', {
                // Same time registration code as above
                validate: {
                  isValidTime: (val: any) => isValidTime(val),
                },
                onChange: (e: any) => handleDateTimechange(e, 'time', ':'),
              })}
              /*@ts-ignore*/
              label={T.translate('case.time')}
              placeholder={T.translate('case.timePlaceholder')}
              value={dateTimeVal.time}
              onKeyDown={(e: any) => isValidKey(e)}
              errors={errors}
              onFocus={(e: any) => setCurrentSelection(e.target.name)}
              autofocus={currentSelection === 'time'}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default EventsDateTimeForm;
